import { assertUnreachable, isDefined } from '@sgme/fp';
import { Button, Icon, Tooltip } from '@sgme/ui';
import { FormattedMessage } from 'react-intl';

import { useOptionSelector } from '@/App/tiles/Option/OptionContext.ts';
import { BidAskInput } from '@/App/tiles/Option/strategy/fields/BidAskInput.tsx';
import { getPremiumFromQuoteLegs, parsePremium } from '@/App/tiles/Option/utils/premiumUtils.ts';
import { useFormatPremium } from '@/App/tiles/Option/utils/useFormatPremium.ts';
import { useUserContext } from '@/context/UserContext.tsx';
import { selectOptionCurrency, selectOptionLegsProperties } from '@/store/option/selectors/optionSelectors.ts';
import { selectCurrencyPairInfo } from '@/store/option/selectors/selectors.ts';
import type { LegsData, OptionLegProperties, PremiumCategory } from '@/store/option/state.ts';
import type { BidAsk } from '@/types/common.ts';

export function Premium({
  leg,
  onChange,
  legData,
}: {
  leg: OptionLegProperties;
  onChange: (bidAsk: BidAsk<number | null>) => void;
  legData: LegsData | undefined;
}) {
  const { premiumType, currencyPair, currency1, currency2, currencyPairPrecision, isSolving } = useOptionSelector(
    (option, state) => {
      const { currencyPairPrecision } = selectCurrencyPairInfo(state, option.properties.tileId);
      const legsProperties = selectOptionLegsProperties(option, leg.legId);
      return {
        premiumType: option.properties.premiumType,
        ...selectOptionCurrency(option),
        currencyPairPrecision,
        isSolving: legsProperties?.strike?.type === 'solving',
      };
    },
  );
  const {
    currentUser: { userType },
  } = useUserContext();
  const isInternalSales = userType === 'Internal Sales';
  const premiumFromQuote = getPremiumFromQuoteLegs(legData, premiumType);

  const premiumCategory = premiumType.premiumCategory;
  const format = useFormatPremium(currencyPair);

  const currencyIndex = premiumType.premiumCurrency;
  const currency = currencyIndex === 1 ? currency1 : currency2;
  const premiumSuffix = getPremiumSuffix(premiumCategory, currency);

  function handleChange({ bid, ask }: BidAsk<number | null>) {
    onChange({
      bid: isDefined(bid) ? parsePremium(bid, premiumCategory, currencyPairPrecision) : null,
      ask: isDefined(ask) ? parsePremium(ask, premiumCategory, currencyPairPrecision) : null,
    });
  }

  function handleUnpin() {
    onChange({ bid: null, ask: null });
  }

  const bid = isSolving ? premiumFromQuote?.bid : (leg.premiumBid ?? premiumFromQuote?.bid);
  const ask = isSolving ? premiumFromQuote?.ask : (leg.premiumAsk ?? premiumFromQuote?.ask);
  return (
    <div className="d-flex flex-grow-1 flex-center">
      <BidAskInput
        editMode="bidAsk"
        data-e2e="premium"
        readOnly={isSolving || !isInternalSales}
        side={leg.side}
        value={{ bid, ask }}
        format={(value) => format(value, premiumCategory)}
        suffix={premiumSuffix}
        onChange={handleChange}
      />
      {(isDefined(leg.premiumBid) || isDefined(leg.premiumAsk)) && !isSolving && (
        <Tooltip contents={<FormattedMessage id="option.pin.tooltip.message" />}>
          <Button onClick={handleUnpin} data-e2e="pin-button" size="sm" purpose="flat" iconPosition="start">
            <Icon icon="push_pin" />
          </Button>
        </Tooltip>
      )}
    </div>
  );
}

/**
 * This is almost like the common getPremiumSuffix() but not quite :'(
 */
function getPremiumSuffix(premiumCategory: PremiumCategory, currency: string) {
  switch (premiumCategory) {
    case 'AMOUNT':
      return ` ${currency}`;
    case 'PERCENT':
      return ` % ${currency}`;
    case 'PPS':
      return ` Pps ${currency}`;
    case 'VOLATILITY':
      return ` ${currency}`;
    default:
      assertUnreachable(premiumCategory, `Unknown premium category: ${premiumCategory}`);
  }
}
